import UsersModel from '@app/models/users'

export const ATSPM_CLICKHOUSE_FLAG = 'signal-operations:atspm-clickhouse'
const OVERRIDE_STORAGE_KEY = 'flow:atspm-clickhouse-override'

export type AtspmMetricScope = 'network' | 'signal' | 'phase' | 'phase-approach' | 'movement' | 'atomic-movement'

// Sigops reads that have a ClickHouse atspm_v3 twin on /api/v2 (server/api/v2/router.js). Anything absent is
// PostgreSQL on v1 whatever the flag says. The scope is part of the lookup because a key alone is
// ambiguous: `tod-charts` is on v2 for phase, movement and atomic-movement, but v1 also serves it at
// network, signal and phase-approach, and those have no v2 route to fall back on.
const V2_METRIC_KEYS: Record<AtspmMetricScope, readonly string[]> = {
	network: [
		'controller-health',
		'coordination-pattern-monitor',
		'cycle-length-monitor',
		'cycle-length-tod',
		'histograms',
		'inferred-timing-plan',
		'offset-tod',
		'tod-schedule',
	],
	signal: [
		'controller-faults',
		'controller-health',
		'detection-health',
		'histograms',
		'offset',
		'pedestrian-timing',
		'phase-timing',
		'preemption',
		'purdue-phase-termination',
		'skips',
		'skips-tod-charts',
		'tod-schedule',
		'transition',
	],
	phase: [
		'atspm-charts-left-turn-gap',
		'atspm-charts-pcd',
		'atspm-charts-red-light',
		'histograms',
		'purdue-split-monitor',
		'red-light-running',
		'skips',
		'skips-tod-charts',
		'tod-charts',
	],
	'phase-approach': ['histograms'],
	movement: ['histograms', 'skips', 'skips-tod-charts', 'tod-charts'],
	'atomic-movement': ['histograms', 'tod-charts'],
}

// Developer Tools switch (super-admins only). On sends this browser's ATSPM reads to ClickHouse whatever the
// customer's flag says; off leaves it to the flag. It lives in the browser, so no other user is affected.
export const isAtspmClickhouseOverrideOn = (): boolean => {
	try {
		return localStorage.getItem(OVERRIDE_STORAGE_KEY) === '1'
	} catch {
		return false
	}
}

export const setAtspmClickhouseOverride = (enabled: boolean): void => {
	try {
		if (enabled) localStorage.setItem(OVERRIDE_STORAGE_KEY, '1')
		else localStorage.removeItem(OVERRIDE_STORAGE_KEY)
	} catch {}
}

// The override counts only for a super-admin, because the server turns every other user away from the
// ClickHouse routes: a stale switch in someone else's browser would otherwise sign them out.
export const isAtspmClickhouseOn = (): boolean =>
	(UsersModel.is_super_admin && isAtspmClickhouseOverrideOn()) || UsersModel.hasFeatureFlag(ATSPM_CLICKHOUSE_FLAG)

export const sigopsApiBase = (scope: AtspmMetricScope, metric_key: string): string =>
	isAtspmClickhouseOn() && V2_METRIC_KEYS[scope].includes(metric_key) ? '/api/v2' : '/api/v1'

// For the ATSPM reads that are not keyed by (scope, metric key): the time-space diagram, its sequences and
// the availability gate.
export const atspmApiBase = (): string => (isAtspmClickhouseOn() ? '/api/v2' : '/api/v1')

// The flag list arrives with /permissions/me; a request built before it does would go to v1 for a flagged
// customer. Waited for once per customer per page load, so a page keeps one routing and an open tab never
// re-fetches the permissions before every chart.
const routing_known_for = new Set<string>()

export const awaitAtspmRouting = async (customer_id?: string): Promise<void> => {
	if (!customer_id || routing_known_for.has(customer_id)) return
	await UsersModel.fetchMe(customer_id)
	routing_known_for.add(customer_id)
}
